import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@dhuttu/ui-components';
import type { UserView } from '../api/users';

/**
 * Per-row "⋯" actions menu. Opens a small floating popover anchored to the
 * IconButton. Closing follows the same outside-click + ESC pattern used by
 * the Combobox in ui-components — kept local rather than extracted because
 * it's a 3-item menu, not a general primitive.
 */
export function CashierRowMenu({
  cashier, onEdit, onSetPassword, onToggleActive,
}: {
  cashier: UserView;
  onEdit: () => void;
  onSetPassword: () => void;
  onToggleActive: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <IconButton
        aria-label={`Actions for ${cashier.displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </IconButton>
      {open && (
        <div
          role="menu"
          className="v2-card absolute right-0 top-[calc(100%+0.25rem)] z-30 flex w-48 flex-col gap-0.5 p-1.5"
        >
          <MenuItem onClick={() => { setOpen(false); onEdit(); }}>Edit</MenuItem>
          <MenuItem onClick={() => { setOpen(false); onSetPassword(); }}>Set offline password</MenuItem>
          <MenuItem onClick={() => { setOpen(false); onToggleActive(); }}>
            {cashier.active ? 'Deactivate' : 'Reactivate'}
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="rounded-md px-2 py-1.5 text-left text-xs text-[color:var(--v2-ink)] hover:bg-[color:var(--v2-sunk)]"
    >
      {children}
    </button>
  );
}
